import { useState } from 'react';
import { Button } from '../../../../components/Button';
import { FilesField, type FileUploadState } from '../../../../components/inputs/FilesField';
import { Modal } from '../../../../components/Modal';
import { useToast } from '../../../../components/toastContext';
import { formatBytes } from '../../../../lib/formatBytes';
import { PRESS_RELEASE_FILE_ACCEPT, PRESS_RELEASE_FILE_HINT, PRESS_RELEASE_FILE_MAX_BYTES } from '../pressReleaseTypes';
import { fileAttachments } from '../utils/fileAttachments';

const DESCRIPTION =
  `Videos (MP4), PDFs and images (PNG or JPEG), up to ${formatBytes(PRESS_RELEASE_FILE_MAX_BYTES)} each, ` +
  "verified against the file's own leading bytes rather than its name.";

/**
 * Attach more files to a press release that already exists — Routing's `<AttachFilesModal>`, for a press release.
 *
 * **No Autogenerate, and deliberately** — the one field is a file whose type the server verifies by
 * its magic bytes, and no textual format can be written honestly into it. The rule's own exemption:
 * the prop is omitted, so no button renders.
 *
 * Each file is its own request; one that fails is named and kept on the form, and the rest still land.
 */
export function AttachFilesModal({
  releaseId,
  releaseTitle,
  onClose,
  onFiled,
}: {
  releaseId: string;
  releaseTitle: string;
  onClose: () => void;
  /** At least one file landed — the list behind refreshes, whether or not the form stays open. */
  onFiled: () => void;
}) {
  const toast = useToast();

  const [files, setFiles] = useState<File[]>([]);
  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [progress, setProgress] = useState(0);
  const [uploads, setUploads] = useState<Record<number, FileUploadState> | undefined>(undefined);

  const failedCount = Object.values(uploads ?? {}).filter((u) => 'error' in u).length;

  const canSave =
    files.length > 0 && !files.some((file) => file.size > PRESS_RELEASE_FILE_MAX_BYTES) && confirmed && !saving;

  async function save() {
    if (!canSave) return;

    setSaving(true);
    setUploads({});
    setProgress(0);

    const failed = await fileAttachments(releaseId, files, setProgress, (index, state) =>
      setUploads((current) => ({ ...current, [index]: state })),
    );
    const filed = files.length - failed.length;

    setSaving(false);

    if (filed > 0) {
      toast.success(`Attached ${filed} ${filed === 1 ? 'file' : 'files'} to "${releaseTitle}".`);
      onFiled();
    }

    if (failed.length === 0) {
      onClose();
      return;
    }

    // Keep only what failed on the form, each with its reason ON its row, so the reader can see what to retry.
    setFiles(failed.map((f) => files[f.index]));
    setUploads(Object.fromEntries(failed.map((f, i) => [i, { error: f.reason }])));
    setConfirmed(false);
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={`Attach files to ${releaseTitle}`}
      size="lg"
      id="press-release-attach-modal"
      confirm={{ id: 'press-release-attach-confirm', checked: confirmed, onChange: setConfirmed, disabled: saving }}
      footer={
        <>
          <Button id="press-release-attach-cancel" variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            id="press-release-attach-submit"
            variant="primary"
            onClick={() => void save()}
            disabled={!canSave}
            loading={saving}
            loadingLabel={`Filing ${progress}%`}
          >
            Submit
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {failedCount > 0 && !saving ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">
              {failedCount === 1 ? 'One file was' : `${failedCount} files were`} not filed — the reason is on{' '}
              {failedCount === 1 ? 'its row' : 'each row'}. Submit again to retry, or remove{' '}
              {failedCount === 1 ? 'it' : 'them'}.
            </p>
          </div>
        ) : null}

        <FilesField
          id="press-release-attach-files"
          label="Files"
          description={DESCRIPTION}
          hint={PRESS_RELEASE_FILE_HINT}
          accept={PRESS_RELEASE_FILE_ACCEPT}
          maxBytes={PRESS_RELEASE_FILE_MAX_BYTES}
          files={files}
          // A row's refusal belongs to that file; once the list changes the indexes no longer line up.
          onChange={(next) => {
            setFiles(next);
            setUploads(undefined);
          }}
          uploads={uploads}
          disabled={saving}
        />
      </div>
    </Modal>
  );
}
